import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getDashboardData,
  DashboardResponse,
  WeekData,
  getWeekQuestions,
  WeekQuestion,
  WeekQuestionsResponse,
  SubmitWeekAnswerResponse,
  SubmitWeekAnswerPayload,
  submitWeekAnswers,
  Week,
  getWeekList,
} from "../../apis/dashboardApis";
import { RootState } from "../../store";

/* =====================================================
   STATE
===================================================== */

interface DashboardState {
  loading: boolean;
  error: string | null;

  weeks: WeekData[];
  selectedWeekId: string | null;

  weekQuestionsLoading: boolean;
  weekQuestionsError: string | null;
  weekQuestions: WeekQuestion[];

  submitLoading: boolean;
  submitError: string | null;
  submitSuccess: boolean;

  weekList: Week[];

  selectedDate: string;
}

const getTodayDate = () => new Date().toLocaleDateString("en-CA");

const initialState: DashboardState = {
  loading: false,
  error: null,

  weeks: [],
  selectedWeekId: null,

  weekQuestionsLoading: false,
  weekQuestionsError: null,
  weekQuestions: [],

  submitLoading: false,
  submitError: null,
  submitSuccess: false,

  weekList: [],

  selectedDate: getTodayDate(),
};

/* =====================================================
   DASHBOARD THUNK
===================================================== */

export const fetchDashboardData = createAsyncThunk<
  WeekData[],
  { startDate: string; endDate: string; weekId?: string },
  { rejectValue: string }
>(
  "dashboard/fetchDashboardData",
  async ({ startDate, endDate, weekId }, { rejectWithValue }) => {
    try {
      const response: DashboardResponse = await getDashboardData(
        startDate,
        endDate,
        weekId,
      );

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.data || "Failed to fetch dashboard data",
      );
    }
  },
);

/* ===== WEEK LIST ===== */
export const fetchWeekListThunk = createAsyncThunk<
  Week[],
  void,
  { rejectValue: string }
>("lesson/fetchWeekList", async (_, { rejectWithValue }) => {
  try {
    const response = await getWeekList();

    if (!response.status) {
      return rejectWithValue("Failed to fetch weeks");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch weeks",
    );
  }
});

/* =====================================================
   WEEK QUESTIONS THUNK
===================================================== */

export const fetchWeekQuestions = createAsyncThunk<
  WeekQuestion[],
  string,
  { rejectValue: string }
>("dashboard/fetchWeekQuestions", async (weekId, { rejectWithValue }) => {
  try {
    const response: WeekQuestionsResponse = await getWeekQuestions(weekId);

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to fetch week questions",
    );
  }
});

/* =====================================================
   SUBMIT WEEK ANSWERS THUNK
===================================================== */

export const submitWeekAnswersThunk = createAsyncThunk<
  SubmitWeekAnswerResponse,
  { weekId: string; payload: SubmitWeekAnswerPayload },
  { rejectValue: string }
>(
  "dashboard/submitWeekAnswers",
  async ({ weekId, payload }, { rejectWithValue }) => {
    try {
      const response = await submitWeekAnswers(weekId, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        // console.log()
        error.response?.data?.data || "Failed to submit answers",
      );
    }
  },
);

/* =====================================================
   SLICE
===================================================== */

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardState: (state) => {
      state.weeks = [];
      state.error = null;
      state.loading = false;
      state.selectedWeekId = null;

      // ✅ clear questions
      state.weekQuestions = [];
      state.weekQuestionsError = null;
      state.weekQuestionsLoading = false;
      state.selectedDate = getTodayDate();
    },

    setSelectedWeek: (state, action: PayloadAction<string>) => {
      state.selectedWeekId = action.payload;

      // Optional: clear old questions when switching week
      state.weekQuestions = [];
    },
    setSelectedFitbitDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ================= DASHBOARD ================= */

      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false;
        state.weeks = action.payload;

        if (action.payload.length > 0) {
          state.selectedWeekId = action.payload[0].weekId;
        }
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      /* ================= WEEK QUESTIONS ================= */

      .addCase(fetchWeekQuestions.pending, (state) => {
        state.weekQuestionsLoading = true;
        state.weekQuestionsError = null;
      })
      .addCase(fetchWeekQuestions.fulfilled, (state, action) => {
        state.weekQuestionsLoading = false;
        state.weekQuestions = action.payload;
      })
      .addCase(fetchWeekQuestions.rejected, (state, action) => {
        state.weekQuestionsLoading = false;
        state.weekQuestionsError = action.payload || "Something went wrong";
      })

      /* ================= SUBMIT ANSWERS ================= */

      .addCase(submitWeekAnswersThunk.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(submitWeekAnswersThunk.fulfilled, (state) => {
        state.submitLoading = false;
        state.submitSuccess = true;
      })
      .addCase(submitWeekAnswersThunk.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload || "Something went wrong";
      })

      /* ===== WEEK LIST ===== */
      .addCase(fetchWeekListThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeekListThunk.fulfilled, (state, action) => {
        state.loading = false;

        // ensure correct order
        state.weekList = action.payload.sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
      })
      .addCase(fetchWeekListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch weeks";
      });
  },
});

/* =====================================================
   SELECTORS
===================================================== */

export const selectSelectedWeek = (state: RootState) => {
  const { weeks, selectedWeekId } = state.dashboard;
  return weeks.find((week) => week.weekId === selectedWeekId) || null;
};

export const selectAllowedWeekIds = (state: RootState) => {
  const weeks = state.dashboard.weeks;

  if (!weeks.length) return [];

  const totalWeeks = weeks[0]?.totalWeeks || [];

  const allowedWeekIds: string[] = [];

  for (let i = 0; i < totalWeeks.length; i++) {
    const week = totalWeeks[i];

    allowedWeekIds.push(week.weekId);

    if (week.weekStatus?.toLowerCase() === "pending") {
      break; // stop at first pending
    }
  }

  return allowedWeekIds;
};

// ✅ Questions selector
export const selectWeekQuestions = (state: RootState) =>
  state.dashboard.weekQuestions;

export const { clearDashboardState, setSelectedWeek, setSelectedFitbitDate } =
  dashboardSlice.actions;

export default dashboardSlice.reducer;
